import React, { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { initGA, disableGA } from '../utils/googleAnalytics'

const CookieBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent')
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setShowBanner(true)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (cookieConsent === 'all') {
      // User hat bereits zugestimmt - Analytics initialisieren
      initGA()
    } else {
      // User hat abgelehnt - Analytics deaktivieren
      disableGA()
    }
  }, [])

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'all')
    setShowBanner(false)
    // Google Analytics initialisieren
    initGA()
  }

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookieConsent', 'necessary')
    setShowBanner(false)
    // Google Analytics deaktivieren
    disableGA()
  }


  if (!showBanner) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Cookie-Einstellungen</h2>
          <button
            onClick={handleAcceptNecessary}
            className="p-1 hover:bg-gray-100 rounded-full"
            title="Nur notwendige Cookies"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-gray-700">
            Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
            Einige Cookies sind technisch notwendig, andere helfen uns dabei, unser Angebot zu verbessern.
          </p>

          {/* Cookie Categories */}
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-green-800">Notwendige Cookies</h3>
                <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">Immer aktiv</span>
              </div>
              <p className="text-sm text-gray-600">
                Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                Sie speichern Ihre Cookie-Einstellungen und Session-Informationen.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">Funktionale Cookies</h3>
              <p className="text-sm text-gray-600 mb-2">
                Diese Cookies ermöglichen erweiterte Funktionen wie die Warenkorb-Speicherung und Benutzereinstellungen.
              </p>
              <div className="text-xs text-gray-500">
                <p><strong>Zweck:</strong> Warenkorb-Status, Benutzereinstellungen</p>
                <p><strong>Speicherdauer:</strong> 30 Tage</p>
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="font-semibold text-indigo-800 mb-2">PayPal-Integration</h3>
              <p className="text-sm text-gray-600 mb-2">
                Cookies von PayPal zur sicheren Zahlungsabwicklung.
              </p>
              <div className="text-xs text-gray-500">
                <p><strong>Anbieter:</strong> PayPal Europe S.à r.l. et Cie, S.C.A.</p>
                <p><strong>Zweck:</strong> Zahlungsabwicklung, Betrugsschutz</p>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800 mb-2">Analytics & Statistik</h3>
              <p className="text-sm text-gray-600 mb-2">
                Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen, um unser Angebot zu verbessern.
              </p>
              <div className="text-xs text-gray-500">
                <p><strong>Anbieter:</strong> Google Analytics (Google Ireland Limited)</p>
                <p><strong>Zweck:</strong> Besucherstatistiken, Nutzerverhalten, Website-Optimierung</p>
                <p><strong>Tracking-ID:</strong> G-MLXWMTB50N</p>
                <p><strong>Speicherdauer:</strong> Bis zu 2 Jahre</p>
                <p><strong>IP-Anonymisierung:</strong> Aktiviert (DSGVO-konform)</p>
              </div>
            </div>
          </div>

          {/* Detailed Information Toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-blue-600 hover:text-blue-800 text-sm underline"
          >
            {showDetails ? 'Weniger anzeigen' : 'Mehr Details zu den Cookies'}
          </button>

          {showDetails && (
            <div className="bg-gray-50 p-4 rounded text-sm text-gray-600">
              <h4 className="font-semibold mb-2">Technische Details:</h4>
              <ul className="space-y-1 text-xs">
                <li><strong>Notwendig:</strong> cookieConsent, sessionId, csrfToken</li>
                <li><strong>Funktional:</strong> cart-data, user-preferences, theme-settings</li>
                <li><strong>PayPal:</strong> PYPF, x-pp-*, tsrce (von PayPal verwaltet)</li>
                <li><strong>Analytics:</strong> _ga, _gid, _gat (Google Analytics)</li>
              </ul>
              <p className="mt-3">
                Sie können Ihre Einwilligung jederzeit widerrufen, indem Sie Ihre Browser-Cookies löschen oder
                die Cookie-Einstellungen in Ihrem Browser anpassen.
              </p>
              <p className="mt-2">
                <strong>Google Analytics Datenschutz:</strong> Wir verwenden Google Analytics mit aktivierter
                IP-Anonymisierung. Ihre IP-Adresse wird gekürzt und nicht vollständig gespeichert.
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleAcceptAll}
            className="flex-1 bg-orange-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
          >
            ✓ Alle Cookies akzeptieren
          </button>
          <button
            onClick={handleAcceptNecessary}
            className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
          >
            🍪 Nur notwendige Cookies
          </button>
        </div>
      </div>
    </div>
  )
}

export default CookieBanner
